import axios from "axios";

// API 基礎 URL - 使用 PHP API
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1/";

// 創建 axios 實例
const api = axios.create({
  baseURL: `${API_BASE_URL}/mathzoo`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10秒超時
});

// 數學動物園 API 服務
export const MathZooAPI = {
  /**
   * 用戶登入
   * 當遊戲開始時：嘗試從MySQL加載用戶進度、遊戲設置、歷史記錄
   * @param {string} username - 用戶名
   * @param {string} password - 數字密碼(4-6位)
   */
  login: async (username, password) => {
    try {
      const response = await api.post("/login.php", { username, password });
      return response.data;
    } catch (error) {
      console.error("Login API error:", error);
      throw error;
    }
  },

  /**
   * 用戶註冊
   * @param {string} username - 用戶名
   * @param {string} password - 數字密碼(4-6位)
   * @param {number|null} parentUserId - 家長用戶ID(可選)
   */
  register: async (username, password, parentUserId = null) => {
    try {
      const response = await api.post("/register.php", {
        username,
        password,
        parentUserId,
      });
      return response.data;
    } catch (error) {
      console.error("Register API error:", error);
      throw error;
    }
  },

  /**
   * 加載用戶進度
   * 當遊戲開始時：嘗試從MySQL加載用戶進度、遊戲設置、歷史記錄
   * IF 無存檔數據 THEN 初始化默認狀態
   * @param {number} userId - 用戶ID
   */
  loadProgress: async (userId) => {
    try {
      const response = await api.get(`/progress.php?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error("Load progress API error:", error);
      throw error;
    }
  },

  /**
   * 保存遊戲進度
   * 當遊戲狀態改變時：自動保存到MySQL（當前分數、星星數量、等級進度、遊戲歷史）
   * @param {number} userId - 用戶ID
   * @param {object} progressData - 進度數據
   */
  saveProgress: async (userId, progressData) => {
    try {
      const response = await api.post(
        `/progress.php?userId=${userId}`,
        progressData
      );
      return response.data;
    } catch (error) {
      console.error("Save progress API error:", error);
      throw error;
    }
  },

  /**
   * 記錄遊戲歷史
   * @param {number} userId - 用戶ID
   * @param {object} gameData - 遊戲數據
   */
  recordGame: async (userId, gameData) => {
    try {
      const response = await api.post(
        `/history.php?userId=${userId}`,
        gameData
      );
      return response.data;
    } catch (error) {
      console.error("Record game API error:", error);
      throw error;
    }
  },

  /**
   * 獲取用戶統計
   * @param {number} userId - 用戶ID
   */
  getStats: async (userId) => {
    try {
      const response = await api.get(`/stats.php?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error("Get stats API error:", error);
      throw error;
    }
  },

  /**
   * 獲取排行榜
   * @param {string} type - 排行類型 (score/stars)
   * @param {number} limit - 限制數量
   */
  getLeaderboard: async (type = "score", limit = 10) => {
    try {
      const response = await api.get("/leaderboard.php", {
        params: { type, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Get leaderboard API error:", error);
      throw error;
    }
  },

  /**
   * 解鎖成就
   * @param {number} userId - 用戶ID
   * @param {string} achievementType - 成就類型
   * @param {object} achievementData - 成就詳細數據
   */
  unlockAchievement: async (userId, achievementType, achievementData = {}) => {
    try {
      const response = await api.post(`/achievements.php?userId=${userId}`, {
        achievementType,
        achievementData,
      });
      return response.data;
    } catch (error) {
      console.error("Unlock achievement API error:", error);
      throw error;
    }
  },

  /**
   * 獲取用戶成就列表
   * @param {number} userId - 用戶ID
   */
  getAchievements: async (userId) => {
    try {
      const response = await api.get(`/achievements.php?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error("Get achievements API error:", error);
      throw error;
    }
  },

  /**
   * 更新遊戲設置
   * @param {number} userId - 用戶ID
   * @param {object} settings - 設置數據
   */
  updateSettings: async (userId, settings) => {
    try {
      const response = await api.put(
        `/settings.php?userId=${userId}`,
        settings
      );
      return response.data;
    } catch (error) {
      console.error("Update settings API error:", error);
      throw error;
    }
  },

  /**
   * 獲取用戶資料
   * @param {number} userId - 用戶ID
   */
  getUser: async (userId) => {
    try {
      const response = await api.get(`/user.php?userId=${userId}`);
      return response.data;
    } catch (error) {
      console.error("Get user API error:", error);
      throw error;
    }
  },

  /**
   * 更新用戶資料
   * @param {number} userId - 用戶ID
   * @param {object} userData - 用戶資料 (如 avatar, username 等)
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await api.post(
        `/user.php?userId=${userId}&action=update`,
        userData
      );
      return response.data;
    } catch (error) {
      console.error("Update user API error:", error);
      throw error;
    }
  },
};

export default MathZooAPI;
